import { NavLink } from 'react-router-dom'
import { Crest } from './Crest'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { orgName, useOrg } from '@/org/OrgProvider'
import type { AppRole } from '@/data/queries'

type BuildState = 'built' | 'partial' | 'proposed'

/**
 * The build state next to each section is deliberate. Half of this
 * platform is running against live data and half is a design, and
 * anyone shown a demo deserves to know which is which without asking.
 */
/**
 * Who sees which section.
 *
 * This is PRESENTATION ONLY. Row level security is the actual boundary and
 * nothing here is relied on for access: a receptionist who typed /people into
 * the address bar would reach a screen that returns nothing, because
 * app.member_org_ids() excludes front_desk from every table read.
 *
 * It matters anyway. A rail full of sections that silently come back empty is
 * how people conclude the software is broken, and reception at six in the
 * morning should see one thing to click, not seven.
 *
 * Right to work is narrowed to HR and admins on top of that, because it is a
 * screenful of immigration status against named people and a HOD has no reason
 * to be there.
 */
const STAFF: AppRole[] = ['group_admin', 'org_admin', 'hr', 'manager', 'viewer']
const HR_ONLY: AppRole[] = ['group_admin', 'org_admin', 'hr']

const SECTIONS: {
  to: string
  label: string
  icon: IconName
  state: BuildState
  roles: AppRole[]
}[] = [
  { to: '/', label: 'Today', icon: 'today', state: 'built', roles: STAFF },
  { to: '/people', label: 'People', icon: 'people', state: 'built', roles: STAFF },
  { to: '/right-to-work', label: 'Right to work', icon: 'rtw', state: 'partial', roles: HR_ONLY },
  {
    to: '/sickness',
    label: 'Log sickness',
    icon: 'absence',
    state: 'built',
    roles: ['front_desk', ...STAFF],
  },
  { to: '/absence', label: 'Sickness', icon: 'absence', state: 'built', roles: STAFF },
  { to: '/toil', label: 'TOIL', icon: 'absence', state: 'built', roles: STAFF },
  { to: '/starters', label: 'Starters', icon: 'starters', state: 'proposed', roles: STAFF },
  { to: '/planday', label: 'Planday sync', icon: 'sync', state: 'built', roles: HR_ONLY },
  { to: '/audit', label: 'Audit', icon: 'audit', state: 'partial', roles: HR_ONLY },
]

const STATE_TITLE: Record<BuildState, string> = {
  built: 'Working against live data',
  partial: 'Back end done, screen pending',
  proposed: 'Designed, not built',
}

export function Rail() {
  const { orgs, current, select } = useOrg()
  const mine = current.roles
  const sections = SECTIONS.filter((s) => mine.some((r) => s.roles.includes(r)))

  return (
    <aside className="rail">
      <div className="crest">
        <Crest width={44} />
        <div className="wordmark">
          TFH&thinsp;&middot;&thinsp;HR
          <span>People &amp; Compliance</span>
        </div>
      </div>

      <div className="orgswitch">
        <div className="eyebrow" style={{ padding: '0 10px 4px' }}>
          Organisation
        </div>
        {orgs.map((org) => (
          <button
            key={org.id}
            type="button"
            aria-current={org.id === current.id}
            onClick={() => select(org.id)}
          >
            <span>{orgName(org)}</span>
            {org.activeHeadcount !== null && <span className="hc">{org.activeHeadcount}</span>}
          </button>
        ))}
      </div>

      <nav className="main" aria-label="Sections">
        {sections.map((s) => (
          <NavLink key={s.to} to={s.to} end={s.to === '/'}>
            <Icon name={s.icon} />
            {s.label}
            <span className={`state ${s.state}`} title={STATE_TITLE[s.state]} />
          </NavLink>
        ))}
      </nav>

      <div className="raillegend">
        {(['built', 'partial', 'proposed'] as const).map((s) => (
          <div key={s}>
            <span className={`state ${s}`} />
            {STATE_TITLE[s]}
          </div>
        ))}
      </div>
    </aside>
  )
}
